import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users as UsersIcon, KeyRound, Save, X, ShieldCheck, RefreshCcw, AlertTriangle, TrendingUp, Package, Coins, BarChart3, Wrench, CheckCircle2, MessageSquare } from "lucide-react";
import { ReminderTemplatesPanel } from "@/pages/Payments";

export default function Settings() {
  const [tab, setTab] = useState("company");
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Admin</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Settings</h1>
      </div>
      <div className="border-b border-zinc-300 flex gap-1 flex-wrap">
        {[
          { k: "company", label: "Company Profile", Icon: Building2 },
          { k: "users", label: "Users & Passwords", Icon: UsersIcon },
          { k: "stock_audit", label: "Stock Audit & Repair", Icon: Wrench },
          { k: "costing", label: "Costing & Stock Value", Icon: Coins },
          { k: "reminders", label: "Reminder Templates", Icon: MessageSquare },
        ].map(({ k, label, Icon }) => (
          <button
            key={k}
            data-testid={`settings-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-mono border-b-2 -mb-px flex items-center gap-2 ${tab === k ? "border-orange-600 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
          ><Icon size={14} /> {label}</button>
        ))}
      </div>
      {tab === "company" && <CompanyTab />}
      {tab === "users" && <UsersTab />}
      {tab === "stock_audit" && <StockAuditTab />}
      {tab === "costing" && <CostingTab />}
      {tab === "reminders" && <RemindersTab />}
    </div>
  );
}

function RemindersTab() {
  return (
    <div data-testid="reminders-tab" className="space-y-2">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Payments</div>
        <h2 className="text-lg font-bold tracking-tight">Reminder Templates</h2>
      </div>
      <ReminderTemplatesPanel />
    </div>
  );
}

function CostingTab() {
  const [sub, setSub] = useState("stock");
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [onlyDispatched, setOnlyDispatched] = useState(false);

  return (
    <div className="space-y-4" data-testid="costing-tab">
      <div className="flex flex-wrap gap-1 border-b border-zinc-200 justify-between items-center">
        <div className="flex flex-wrap gap-1">
          {[
            { k: "stock", label: "Stock On Hand", Icon: Package },
            { k: "lots", label: "Lots Ledger (FIFO Queue)", Icon: BarChart3 },
            { k: "usage", label: "Raw Material Usage", Icon: TrendingUp },
            { k: "log", label: "Usage Log (per-event)", Icon: BarChart3 },
            { k: "carton", label: "Cost Per Carton", Icon: Coins },
          ].map(({ k, label, Icon }) => (
            <button key={k} onClick={() => setSub(k)} data-testid={`costing-sub-${k}`}
              className={`px-3 py-2 text-[11px] uppercase font-mono tracking-wider flex items-center gap-1.5 border-b-2 -mb-px ${sub === k ? "border-orange-600 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>
      </div>

      {(sub === "usage" || sub === "carton" || sub === "log") && (
        <div className="flex flex-wrap items-end gap-2 bg-zinc-50 border border-zinc-200 px-3 py-2">
          <div>
            <label className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 block">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 px-2 text-xs border border-zinc-300 font-mono" data-testid="costing-from" />
          </div>
          <div>
            <label className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 block">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 px-2 text-xs border border-zinc-300 font-mono" data-testid="costing-to" />
          </div>
          {sub === "carton" && (
            <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-700 flex items-center gap-1.5">
              <input type="checkbox" checked={onlyDispatched} onChange={(e) => setOnlyDispatched(e.target.checked)} className="accent-orange-600" data-testid="costing-only-dispatched" />
              Dispatched only
            </label>
          )}
        </div>
      )}

      {sub === "stock" && <StockOnHandPanel />}
      {sub === "lots" && <LotsLedgerPanel />}
      {sub === "usage" && <RawUsagePanel from={from} to={to} />}
      {sub === "log" && <UsageLogPanel from={from} to={to} />}
      {sub === "carton" && <CostPerCartonPanel from={from} to={to} onlyDispatched={onlyDispatched} />}
    </div>
  );
}

function UsageLogPanel({ from, to }) {
  const [data, setData] = useState(null);
  const [filterRm, setFilterRm] = useState("");
  const [rms, setRms] = useState([]);
  useEffect(() => { api.get("/raw-materials").then((r) => setRms(r.data || [])); }, []);
  useEffect(() => {
    const q = new URLSearchParams({ start: from, end: to });
    if (filterRm) q.set("rm_id", filterRm);
    api.get(`/reports/usage-log?${q.toString()}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load"));
  }, [from, to, filterRm]);
  if (!data) return <div className="text-sm font-mono text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="panel-usage-log">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 block">Filter material</label>
          <select value={filterRm} onChange={(e) => setFilterRm(e.target.value)} className="h-8 px-2 text-xs border border-zinc-300 font-mono">
            <option value="">All materials</option>
            {rms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div className="ml-auto text-[11px] font-mono text-zinc-600">
          <b>{data.count}</b> events · total qty <b>{Number(data.total_qty).toFixed(3)}</b> · total cost <b className="text-orange-700">₹{Number(data.total_cost).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</b>
        </div>
      </div>
      <div className="bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Material</th>
              <th className="text-left px-3 py-2">Bucket</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Cost</th>
              <th className="text-left px-3 py-2">Carton</th>
              <th className="text-left px-3 py-2">By</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr><td colSpan="7" className="px-3 py-6 text-center text-zinc-500 text-sm">No consumption events.</td></tr>
            ) : data.rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-200" data-testid={`usage-log-row-${i}`}>
                <td className="px-3 py-2 text-[11px] font-mono text-zinc-600">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2">{r.material}</td>
                <td className="px-3 py-2 text-[10px] font-mono uppercase text-zinc-700">{r.bucket || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{Number(r.qty).toFixed(3)} {r.unit}</td>
                <td className="px-3 py-2 text-right font-mono text-orange-700 font-semibold">₹{Number(r.cost).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2 text-[11px] font-mono">{r.carton_no || "—"}</td>
                <td className="px-3 py-2 text-[10px] font-mono text-zinc-500">{r.by || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StockOnHandPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/stock-on-hand").then((r) => setData(r.data)).catch(() => toast.error("Could not load")); }, []);
  if (!data) return <div className="text-sm font-mono text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="panel-stock-on-hand">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-orange-50 border border-orange-300 px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Grand Total Value</div>
          <div className="text-3xl font-bold text-orange-900 mt-0.5" data-testid="stock-grand-total">₹{Number(data.grand_total_value).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
          <div className="text-[10px] font-mono text-orange-700 mt-1">Σ (remaining × price/unit) · ex-GST</div>
        </div>
        <div className="bg-zinc-50 border border-zinc-300 px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">Value incl. GST</div>
          <div className="text-2xl font-bold text-zinc-800 mt-0.5" data-testid="stock-grand-total-incl">₹{Number(data.grand_total_value_incl_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
          <div className="text-[10px] font-mono text-zinc-500 mt-1">{Object.entries(data.qty_by_unit || {}).map(([u, q]) => `${Number(q).toFixed(3)} ${u}`).join(" · ")}</div>
        </div>
      </div>
      <div className="bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2">Material</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-right px-3 py-2">Qty in lots</th>
              <th className="text-right px-3 py-2">Avg ₹/unit</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-right px-3 py-2">Incl GST</th>
              <th className="text-right px-3 py-2">Lots</th>
            </tr>
          </thead>
          <tbody>
            {data.materials.filter((m) => m.category !== "paper_tube").map((m) => (
              <tr key={m.rm_id} className="border-t border-zinc-200" data-testid={`stock-row-${m.rm_id}`}>
                <td className="px-3 py-2 font-medium">{m.material}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-zinc-600">{m.category}</td>
                <td className="px-3 py-2 text-right font-mono">{Number(m.lots_remaining_qty).toFixed(3)} {m.unit}</td>
                <td className="px-3 py-2 text-right font-mono">₹{Number(m.avg_price_per_unit).toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-orange-700">₹{Number(m.value).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2 text-right font-mono text-zinc-600">₹{Number(m.value_incl_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2 text-right font-mono text-zinc-600">{m.lots_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LotsLedgerPanel() {
  const [data, setData] = useState(null);
  const [editingLot, setEditingLot] = useState(null);
  const [showConsumed, setShowConsumed] = useState(false);
  const [category, setCategory] = useState("");
  const [rmId, setRmId] = useState("");
  const [materials, setMaterials] = useState([]);

  // Load the RM dropdown once.
  useEffect(() => {
    api.get("/raw-materials").then((r) => setMaterials(r.data || [])).catch(() => {});
  }, []);

  const reload = () => {
    const params = new URLSearchParams();
    if (showConsumed) params.set("show_consumed", "true");
    if (category) params.set("category", category);
    if (rmId) params.set("rm_id", rmId);
    const qs = params.toString();
    api.get(`/reports/lots-ledger${qs ? `?${qs}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load"));
  };
  useEffect(reload, [showConsumed, category, rmId]);

  if (!data) return <div className="text-sm font-mono text-zinc-500">Loading…</div>;

  const cats = [...new Set(materials.map((m) => m.category).filter(Boolean))].sort();
  const filteredMaterials = category ? materials.filter((m) => m.category === category) : materials;

  const totalOriginal = data.reduce((s, l) => s + Number(l.original_qty || 0), 0);
  const totalRemaining = data.reduce((s, l) => s + Number(l.remaining || 0), 0);
  const totalValueIncl = data.reduce((s, l) => s + Number(l.value_incl_gst || 0), 0);

  return (
    <div className="space-y-3" data-testid="panel-lots-ledger">
      {/* Filter bar */}
      <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 flex items-center flex-wrap gap-3 text-xs">
        <label className="flex items-center gap-1.5 font-mono uppercase tracking-wider text-[10px] text-zinc-600">
          Category
          <select data-testid="lots-filter-category" value={category} onChange={(e) => { setCategory(e.target.value); setRmId(""); }}
            className="border border-zinc-300 px-2 h-7 text-[11px] bg-white">
            <option value="">All</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 font-mono uppercase tracking-wider text-[10px] text-zinc-600">
          Material
          <select data-testid="lots-filter-material" value={rmId} onChange={(e) => setRmId(e.target.value)}
            className="border border-zinc-300 px-2 h-7 text-[11px] bg-white min-w-[200px]">
            <option value="">All</option>
            {filteredMaterials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 font-mono uppercase tracking-wider text-[10px] text-zinc-700">
          <input type="checkbox" data-testid="lots-show-consumed" checked={showConsumed}
            onChange={(e) => setShowConsumed(e.target.checked)} />
          Include fully-consumed lots
        </label>
        <div className="ml-auto text-[10px] font-mono text-zinc-600">
          {data.length} lot{data.length !== 1 ? "s" : ""} · Received {totalOriginal.toFixed(3)} · Remaining <strong>{totalRemaining.toFixed(3)}</strong> · Value <strong>₹{totalValueIncl.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm text-zinc-600">No lots match these filters.</div>
      ) : (
        <div className="bg-white border border-zinc-300 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
              <tr>
                <th className="text-left px-3 py-2">Material</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Received</th>
                <th className="text-right px-3 py-2">Original</th>
                <th className="text-right px-3 py-2">Consumed</th>
                <th className="text-right px-3 py-2">Remaining</th>
                <th className="text-right px-3 py-2">₹/unit</th>
                <th className="text-right px-3 py-2">GST</th>
                <th className="text-right px-3 py-2">Value incl GST</th>
                <th className="text-left px-3 py-2">Supplier</th>
                <th className="text-center px-3 py-2">Status</th>
                <th className="text-center px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((l) => (
                <tr key={l.lot_id} className={`border-t border-zinc-200 ${l.is_open ? "" : "bg-zinc-50"}`} data-testid={`lot-${l.lot_id}`}>
                  <td className="px-3 py-2 font-medium">{l.material}</td>
                  <td className="px-3 py-2 text-[11px] font-mono text-zinc-600">{l.category}</td>
                  <td className="px-3 py-2 text-[11px] font-mono text-zinc-600">{(l.received_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right font-mono">{Number(l.original_qty).toFixed(3)}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-600">{Number(l.consumed_qty || 0).toFixed(3)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${l.is_open ? "text-orange-700" : "text-zinc-400"}`}>{Number(l.remaining).toFixed(3)} {l.unit}</td>
                  <td className="px-3 py-2 text-right font-mono">₹{Number(l.price_per_unit).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-600">{l.gst_pct}%</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-600">₹{Number(l.value_incl_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-[11px] font-mono text-zinc-600">{l.supplier_name || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {l.is_open ? (
                      <span className="inline-block text-[9px] font-mono uppercase bg-emerald-50 text-emerald-800 border border-emerald-300 px-1.5 py-0.5">Open</span>
                    ) : (
                      <span className="inline-block text-[9px] font-mono uppercase bg-zinc-100 text-zinc-600 border border-zinc-300 px-1.5 py-0.5">Used</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      data-testid={`edit-lot-${l.lot_id}`}
                      onClick={() => setEditingLot(l)}
                      className="text-[10px] font-mono uppercase tracking-wider border border-zinc-300 px-2 py-1 hover:bg-zinc-100"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingLot && (
        <EditLotModal
          lot={editingLot}
          onClose={() => setEditingLot(null)}
          onSaved={() => { setEditingLot(null); reload(); }}
        />
      )}
    </div>
  );
}

function EditLotModal({ lot, onClose, onSaved }) {
  const [quantity, setQuantity] = useState(lot.original_qty);
  const [ppu, setPpu] = useState(lot.price_per_unit);
  const [gstPct, setGstPct] = useState(lot.gst_pct);
  const [supName, setSupName] = useState(lot.supplier_name || "");
  const [supGst, setSupGst] = useState(lot.supplier_gst || "");
  const [supAddr, setSupAddr] = useState(lot.supplier_address || "");
  const [receivedAt, setReceivedAt] = useState((lot.received_at || "").slice(0, 10));
  const [note, setNote] = useState(lot.note || "");
  const [busy, setBusy] = useState(false);

  const totalEx = quantity * ppu;
  const totalIncl = totalEx * (1 + (gstPct || 0) / 100);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/raw-materials/lots/${lot.lot_id}`, {
        quantity: Number(quantity),
        price_per_unit: Number(ppu),
        gst_pct: Number(gstPct),
        supplier_name: supName.trim(),
        supplier_gst: supGst.trim(),
        supplier_address: supAddr.trim(),
        received_at: receivedAt ? new Date(receivedAt).toISOString() : null,
        note: note.trim(),
      });
      toast.success("Lot updated. Run Master Fix to refresh stock totals.");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete this lot of ${lot.material}? This cannot be undone. Run Master Fix afterward.`)) return;
    setBusy(true);
    try {
      await api.delete(`/raw-materials/lots/${lot.lot_id}`);
      toast.success("Lot deleted.");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-6" data-testid="edit-lot-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-lg mx-3">
        <div className="flex items-center justify-between border-b border-zinc-300 px-5 py-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Edit Lot</div>
            <h2 className="text-base font-semibold">{lot.material}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={save} className="px-5 py-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Received Date">
              <input data-testid="edit-lot-received-at" type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 font-mono text-sm" />
            </Field>
            <Field label={`Quantity (${lot.unit})`}>
              <input data-testid="edit-lot-qty" type="number" step="0.001" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 font-mono text-sm" />
            </Field>
            <Field label="Price / unit (₹, ex-GST)">
              <input data-testid="edit-lot-ppu" type="number" step="0.01" value={ppu} onChange={(e) => setPpu(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 font-mono text-sm" />
            </Field>
            <Field label="GST %">
              <input data-testid="edit-lot-gst" type="number" step="0.01" value={gstPct} onChange={(e) => setGstPct(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 font-mono text-sm" />
            </Field>
          </div>
          <Field label="Supplier Name">
            <input data-testid="edit-lot-supplier" value={supName} onChange={(e) => setSupName(e.target.value)}
              className="w-full h-9 px-2 border border-zinc-300 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Supplier GSTIN">
              <input value={supGst} onChange={(e) => setSupGst(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 font-mono text-sm" />
            </Field>
            <Field label="Supplier Address">
              <input value={supAddr} onChange={(e) => setSupAddr(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 text-sm" />
            </Field>
          </div>
          <Field label="Note">
            <input value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full h-9 px-2 border border-zinc-300 text-sm" />
          </Field>
          <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 grid grid-cols-3 gap-2 text-[11px] font-mono">
            <div><span className="text-zinc-500">ex-GST: </span>₹{Number(totalEx).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
            <div><span className="text-zinc-500">GST: </span>₹{Number(totalIncl - totalEx).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
            <div><span className="text-zinc-500">incl GST: </span><strong>₹{Number(totalIncl).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></div>
          </div>
          <div className="text-[10px] font-mono text-orange-700 bg-orange-50 border border-orange-200 px-3 py-1.5">
            After editing, open Settings → Stock Audit & Repair → <strong>Run Master Fix</strong> to refresh stock totals.
          </div>
        </form>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-between">
          <button onClick={del} disabled={busy} data-testid="delete-lot-btn"
            className="px-4 py-2 text-xs uppercase font-mono border border-red-400 text-red-700 hover:bg-red-50 disabled:opacity-50">
            Delete Lot
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy} className="px-4 py-2 text-xs uppercase font-mono border border-zinc-300 hover:bg-zinc-100">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="save-lot-btn"
              className="px-5 py-2 text-xs uppercase font-mono bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">
              {busy ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function RawUsagePanel({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/reports/raw-usage?start=${from}&end=${to}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load"));
  }, [from, to]);
  if (!data) return <div className="text-sm font-mono text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="panel-raw-usage">
      <div className="bg-orange-50 border border-orange-300 px-4 py-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Total Raw Material Cost ({from} → {to})</div>
        <div className="text-3xl font-bold text-orange-900 mt-0.5" data-testid="usage-grand-total">₹{Number(data.grand_total_cost_incl_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
      </div>
      <div className="bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2">Material</th>
              <th className="text-right px-3 py-2">Qty consumed</th>
              <th className="text-right px-3 py-2">Cost (incl GST)</th>
              <th className="text-right px-3 py-2">Entries</th>
            </tr>
          </thead>
          <tbody>
            {data.materials.length === 0 ? (
              <tr><td colSpan="4" className="px-3 py-6 text-center text-zinc-500 text-sm">No consumption in this range.</td></tr>
            ) : data.materials.map((m) => (
              <tr key={m.rm_id} className="border-t border-zinc-200">
                <td className="px-3 py-2 font-medium">{m.material}</td>
                <td className="px-3 py-2 text-right font-mono">{Number(m.qty_used).toFixed(3)} {m.unit}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-orange-700">₹{Number(m.cost_incl_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2 text-right font-mono text-zinc-600">{m.entries}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] font-mono text-zinc-500">Tip: cost is captured per consumption event using FIFO lot prices. Legacy consumption events (created before lots existed) show ₹0.</div>
    </div>
  );
}

function CostPerCartonPanel({ from, to, onlyDispatched }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    const qs = new URLSearchParams({ start: from, end: to, only_dispatched: String(onlyDispatched) }).toString();
    api.get(`/reports/cost-per-carton?${qs}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load"));
  }, [from, to, onlyDispatched]);
  if (!data) return <div className="text-sm font-mono text-zinc-500">Loading…</div>;

  // Compute grand totals per component across all cartons in range.
  const totals = {};
  data.cartons.forEach((c) => {
    Object.entries(c.breakdown || {}).forEach(([k, v]) => {
      const row = totals[k] = totals[k] || { qty: 0, unit: v.unit, cost: 0 };
      row.qty += v.qty; row.cost += v.cost;
    });
  });
  const ORDER = ["Granule", "Master Batch", "Paper Tube", "Carton Box"];
  const components = [...ORDER, ...Object.keys(totals).filter((k) => !ORDER.includes(k))].filter((k) => totals[k]);

  return (
    <div className="space-y-3" data-testid="panel-cost-per-carton">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-orange-50 border border-orange-300 px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Total Material Cost (incl GST)</div>
          <div className="text-2xl font-bold text-orange-900 mt-0.5" data-testid="carton-grand-total">₹{Number(data.grand_total_cost_incl_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="bg-zinc-50 border border-zinc-300 px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">Cartons in range</div>
          <div className="text-2xl font-bold text-zinc-900 mt-0.5">{data.total_cartons}</div>
        </div>
      </div>

      {components.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="component-totals">
          {components.map((k) => (
            <div key={k} className="bg-white border border-zinc-300 px-3 py-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
              <div className="font-semibold text-zinc-900 mt-0.5">₹{Number(totals[k].cost).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
              <div className="text-[10px] font-mono text-zinc-500">{Number(totals[k].qty).toFixed(3)} {totals[k].unit}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2 w-6"></th>
              <th className="text-left px-3 py-2">Box No.</th>
              <th className="text-left px-3 py-2">SKU</th>
              <th className="text-right px-3 py-2">Net (kg)</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Total Cost</th>
              <th className="text-right px-3 py-2">₹/kg</th>
            </tr>
          </thead>
          <tbody>
            {data.cartons.length === 0 ? (
              <tr><td colSpan="7" className="px-3 py-6 text-center text-zinc-500 text-sm">No cartons in this range.</td></tr>
            ) : data.cartons.map((c) => {
              const open = expanded[c.box_no];
              return (
                <React.Fragment key={c.box_no}>
                  <tr className="border-t border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => setExpanded({ ...expanded, [c.box_no]: !open })} data-testid={`carton-row-${c.box_no}`}>
                    <td className="px-3 py-2 text-zinc-400">{open ? "▾" : "▸"}</td>
                    <td className="px-3 py-2 font-mono">{c.box_no}</td>
                    <td className="px-3 py-2">{c.sku}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(c.net_weight_kg).toFixed(3)}</td>
                    <td className="px-3 py-2 text-[10px] font-mono uppercase">{c.status}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-orange-700">₹{Number(c.material_cost_incl_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-600">₹{Number(c.cost_per_kg).toFixed(2)}</td>
                  </tr>
                  {open && (
                    <tr className="bg-orange-50/40" data-testid={`carton-breakdown-${c.box_no}`}>
                      <td></td>
                      <td colSpan="6" className="px-3 py-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          {Object.entries(c.breakdown || {}).length === 0 ? (
                            <div className="text-zinc-500 col-span-4 italic">No FIFO lot data for this carton (likely produced before lot pricing existed).</div>
                          ) : Object.entries(c.breakdown).map(([k, v]) => (
                            <div key={k} className="border border-zinc-300 bg-white px-2 py-1.5">
                              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
                              <div className="font-mono">{Number(v.qty).toFixed(3)} {v.unit}</div>
                              <div className="text-orange-700 font-mono font-semibold">₹{Number(v.cost).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReconcileTab() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/raw-materials/diagnose")
      .then((r) => setDiag(r.data))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (!window.confirm("This will deduct raw material stock for every carton that hasn't been reconciled yet. Continue?")) return;
    setRunning(true);
    try {
      const r = await api.post("/raw-materials/reconcile");
      toast.success(`Reconciled ${r.data.cartons_reconciled} carton${r.data.cartons_reconciled === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  if (loading || !diag) return <div className="text-sm font-mono text-zinc-500" data-testid="reconcile-loading">Loading diagnosis…</div>;

  const hasPending = (diag.cartons_pending || 0) > 0;

  return (
    <div className="space-y-4" data-testid="reconcile-tab">
      <div className="bg-orange-50 border border-orange-300 px-4 py-3 text-xs text-zinc-800 leading-relaxed">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-orange-700 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-orange-900 mb-1">What this does</div>
            <div>Older cartons (created before the auto-deduction rule) may not have decreased your raw-material stock. This panel shows what would be deducted today and lets you run a one-click reconcile so your raw-material inventory matches the cartons you have actually produced.</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="reconcile-stats">
        <Stat label="Cartons pending" value={diag.cartons_pending} highlight={hasPending} />
        <Stat label="Cartons skipped (no recipe)" value={diag.cartons_skipped_no_recipe} warning={(diag.cartons_skipped_no_recipe || 0) > 0} />
        <Stat label="Raw materials affected" value={(diag.by_raw_material || []).length} />
      </div>

      {hasPending && (
        <div className="bg-white border border-zinc-300">
          <div className="px-4 py-2 border-b border-zinc-300 text-[10px] font-mono uppercase tracking-widest text-zinc-600">Will deduct from</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                <tr>
                  <th className="text-left px-3 py-2">Raw Material</th>
                  <th className="text-right px-3 py-2">Quantity to deduct</th>
                  <th className="text-right px-3 py-2">Cartons</th>
                </tr>
              </thead>
              <tbody>
                {(diag.by_raw_material || []).map((r) => (
                  <tr key={r.rm_id} className="border-t border-zinc-200" data-testid={`reconcile-rm-${r.rm_id}`}>
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-right font-mono text-orange-700">−{Number(r.to_deduct).toFixed(3)}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-600">{r.cartons}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(diag.incomplete_products || []).length > 0 && (
        <div className="bg-white border border-amber-300">
          <div className="px-4 py-2 border-b border-amber-300 bg-amber-50 text-[10px] font-mono uppercase tracking-widest text-amber-900">Products with incomplete recipe (fix before reconciling — these cartons will be skipped)</div>
          <ul className="px-4 py-2 text-xs space-y-1">
            {diag.incomplete_products.map((p) => (
              <li key={p.id} className="font-mono">
                <span className="font-semibold">{p.sku}</span> — missing: {p.missing.join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          data-testid="reconcile-run-btn"
          onClick={run}
          disabled={!hasPending || running}
          className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-xs uppercase tracking-wider font-mono flex items-center gap-2 disabled:opacity-40"
        >
          <RefreshCcw size={14} className={running ? "animate-spin" : ""} />
          {running ? "Reconciling…" : hasPending ? `Run Reconcile (${diag.cartons_pending} cartons)` : "Nothing to reconcile"}
        </button>
        <button
          data-testid="reconcile-refresh-btn"
          onClick={load}
          className="border border-zinc-300 bg-white text-zinc-700 px-4 py-2.5 text-xs uppercase tracking-wider font-mono hover:bg-zinc-50"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

function StockAuditTab() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [retropricing, setRetropricing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [openConsumers, setOpenConsumers] = useState({}); // rm_id -> {loading, data}

  const load = () => {
    setLoading(true);
    api.get("/raw-materials/audit")
      .then((r) => setAudit(r.data))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const recomputeFromTruth = async () => {
    if (!window.confirm(
      "⚡ ONE-CLICK STOCK FIX\n\n" +
      "Applies the authoritative formula:\n" +
      "  Stock = Total Received − Used by alive cartons\n\n" +
      "What this does:\n" +
      "• Permanently deletes inventory logs of cartons that no longer exist (deleted cartons)\n" +
      "• Rebuilds each lot's remaining quantity from the FIFO replay\n" +
      "• Sets every raw material's Stock to the exact computed value\n\n" +
      "After this runs, the Stock Audit table will show 0 drift across the board.\n\nProceed?"
    )) return;
    setRecomputing(true);
    setLastResult(null);
    try {
      const r = await api.post("/raw-materials/recompute-from-truth");
      setLastResult({ kind: "recompute", data: r.data });
      toast.success(
        `Stock recomputed for ${r.data.materials_recomputed} material(s); ` +
        `${r.data.materials_changed} changed; ` +
        `${r.data.orphan_logs_deleted} orphan log(s) purged.`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setRecomputing(false);
    }
  };

  const cleanOrphans = async () => {
    if (!window.confirm(
      "This refunds every raw_out inventory log whose carton no longer exists in the system (i.e. previously deleted cartons). It will:\n\n" +
      "• Restore each lot's remaining qty\n" +
      "• Add back stock to the raw material\n" +
      "• Delete the orphan logs\n\n" +
      "Safe to re-run. Proceed?"
    )) return;
    setCleaningOrphans(true);
    setLastResult(null);
    try {
      const r = await api.post("/raw-materials/clean-orphans");
      setLastResult({ kind: "orphans", data: r.data });
      toast.success(`Cleaned ${r.data.orphan_cartons_cleaned} orphan carton(s), refunded ${r.data.qty_refunded} units`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setCleaningOrphans(false);
    }
  };

  const rebuildStock = async () => {
    if (!window.confirm(
      "This forces every raw material's displayed Stock to equal the sum of remaining qty across all its received Lots. Proceed?"
    )) return;
    setRebuilding(true);
    setLastResult(null);
    try {
      const r = await api.post("/raw-materials/rebuild-stock-from-lots");
      setLastResult({ kind: "rebuild", data: r.data });
      toast.success(`Rebuilt stock for ${r.data.materials_updated} material(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setRebuilding(false);
    }
  };

  const retropriceLogs = async () => {
    if (!window.confirm(
      "Re-price every zero-cost consumption log (cartons made before any lot was received) against the lots currently available, FIFO.\n\n" +
      "• Stamps cost on those consumption logs\n" +
      "• Burns down each lot's `remaining` accordingly\n" +
      "• Does NOT change displayed Stock totals\n\n" +
      "Cost Sheets & Cost-Per-Carton reports will then show real numbers. Proceed?"
    )) return;
    setRetropricing(true);
    setLastResult(null);
    try {
      const r = await api.post("/raw-materials/retroprice-zero-cost-logs");
      setLastResult({ kind: "retroprice", data: r.data });
      toast.success(
        `Re-priced ${r.data.logs_priced} log(s) across ${r.data.per_rm.length} material(s); ` +
        `₹${Number(r.data.cost_added).toLocaleString("en-IN", { minimumFractionDigits: 2 })} added to historical costs.`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setRetropricing(false);
    }
  };

  if (loading || !audit) {
    return <div className="text-sm font-mono text-zinc-500" data-testid="stock-audit-loading">Loading audit…</div>;
  }

  const loadConsumers = async (rmId) => {
    setOpenConsumers((s) => ({ ...s, [rmId]: { loading: true } }));
    try {
      const r = await api.get(`/raw-materials/${rmId}/consumers`);
      setOpenConsumers((s) => ({ ...s, [rmId]: { loading: false, data: r.data } }));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setOpenConsumers((s) => {
        const { [rmId]: _gone, ...rest } = s;
        return rest;
      });
    }
  };

  const toggleConsumers = (rmId) => {
    if (openConsumers[rmId]) {
      setOpenConsumers((s) => {
        const { [rmId]: _gone, ...rest } = s;
        return rest;
      });
    } else {
      loadConsumers(rmId);
    }
  };

  const drifted = (audit.materials || []).filter((m) => Math.abs(m.delta) > 0.001);
  const inSync = (audit.materials || []).length - drifted.length;
  const busy = recomputing || cleaningOrphans || rebuilding || retropricing;

  return (
    <div className="space-y-4" data-testid="stock-audit-tab">
      {/* Master fix */}
      <div className="bg-orange-50 border-2 border-orange-500 px-4 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700 mb-1">⚡ Master Fix · One Click</div>
            <div className="text-sm font-semibold text-zinc-950 mb-1">Recompute Stock from Truth</div>
            <div className="text-xs text-zinc-700 leading-relaxed">
              <strong>Stock = Total Received − Used by alive cartons.</strong> Legacy non-carton
              consumption (old production-entry / pre-tagging logs) is <em>ignored</em>. Permanently
              deletes logs of deleted cartons, rebuilds FIFO lot remaining, and writes the exact
              computed Stock to every raw material. Run this after deletions to wipe drift in one shot.
            </div>
          </div>
          <button
            data-testid="recompute-truth-btn"
            onClick={recomputeFromTruth}
            disabled={busy}
            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 text-xs uppercase tracking-wider font-mono flex items-center gap-2 disabled:opacity-40 self-center"
          >
            <Wrench size={14} className={recomputing ? "animate-spin" : ""} />
            {recomputing ? "Recomputing…" : "Run Master Fix"}
          </button>
        </div>
      </div>

      {/* Re-price historical cartons */}
      <div className="bg-emerald-50 border border-emerald-400 px-4 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-1">💰 Cost-Sheet Fix · Re-price Historical Cartons</div>
            <div className="text-sm font-semibold text-zinc-950 mb-1">Retro-price zero-cost cartons</div>
            <div className="text-xs text-zinc-700 leading-relaxed">
              Cartons made <em>before</em> any lot was received logged their consumption with <strong>cost = ₹0</strong>.
              This sweep walks every zero-cost log against the lots currently available and FIFO-prices them at the lot rate
              (+ GST). After running, <strong>Order Cost Sheet</strong> and <strong>Cost-Per-Carton</strong> reports will show
              real numbers. Stock totals are not changed.
            </div>
          </div>
          <button
            data-testid="retroprice-btn"
            onClick={retropriceLogs}
            disabled={busy}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 text-xs uppercase tracking-wider font-mono flex items-center gap-2 disabled:opacity-40 self-center"
          >
            <Coins size={14} className={retropricing ? "animate-spin" : ""} />
            {retropricing ? "Re-pricing…" : "Re-price Old Cartons"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="audit-stats">
        <Stat label="Materials drifted" value={drifted.length} highlight={drifted.length > 0} />
        <Stat label="Materials in sync" value={inSync} />
        <Stat label="Total absolute drift" value={Number(audit.total_abs_drift || 0).toFixed(3)} warning={audit.total_abs_drift > 0.001} />
      </div>

      {/* Audit table */}
      <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2">Raw Material</th>
              <th className="text-right px-3 py-2">Live Stock</th>
              <th className="text-right px-3 py-2">Sum of Lots Remaining</th>
              <th className="text-right px-3 py-2">Delta (Stock − Lots)</th>
              <th className="text-center px-3 py-2">State</th>
              <th className="text-center px-3 py-2">Trace</th>
            </tr>
          </thead>
          <tbody>
            {(audit.materials || []).length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-zinc-500 font-mono">All raw materials have 0 stock and 0 lots — nothing to audit yet.</td></tr>
            )}
            {(audit.materials || []).map((m) => {
              const drift = Math.abs(m.delta) > 0.001;
              const consumers = openConsumers[m.rm_id];
              return (
                <React.Fragment key={m.rm_id}>
                  <tr className="border-t border-zinc-200" data-testid={`audit-row-${m.rm_id}`}>
                    <td className="px-3 py-2 font-medium">{m.name}</td>
                    <td className={`px-3 py-2 text-right font-mono ${m.stock < 0 ? "text-red-700" : "text-zinc-900"}`}>{Number(m.stock).toFixed(3)} {m.unit}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-700">{Number(m.lots_remaining).toFixed(3)} {m.unit}</td>
                    <td className={`px-3 py-2 text-right font-mono ${drift ? "text-orange-700 font-semibold" : "text-zinc-400"}`}>
                      {m.delta > 0 ? "+" : ""}{Number(m.delta).toFixed(3)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {drift ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-orange-800 bg-orange-100 px-2 py-0.5 border border-orange-300">
                          <AlertTriangle size={10} /> Drift
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-800 bg-emerald-50 px-2 py-0.5 border border-emerald-300">
                          <CheckCircle2 size={10} /> Synced
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        data-testid={`view-consumers-${m.rm_id}`}
                        onClick={() => toggleConsumers(m.rm_id)}
                        className="text-[10px] font-mono uppercase tracking-wider border border-zinc-300 px-2 py-1 hover:bg-zinc-100"
                      >
                        {consumers ? "Hide" : "View Consumers"}
                      </button>
                    </td>
                  </tr>
                  {consumers && (
                    <tr className="bg-zinc-50 border-t border-zinc-200" data-testid={`consumers-detail-${m.rm_id}`}>
                      <td colSpan={6} className="px-4 py-3">
                        {consumers.loading ? (
                          <div className="text-xs font-mono text-zinc-500">Loading consumer trace…</div>
                        ) : <ConsumerDetail data={consumers.data} />}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Advanced / fallback actions */}
      <details className="bg-white border border-zinc-200 px-4 py-3">
        <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-widest text-zinc-600">Advanced (two-step fallback)</summary>
        <div className="flex items-center gap-2 flex-wrap mt-3">
          <button
            data-testid="clean-orphans-btn"
            onClick={cleanOrphans}
            disabled={busy}
            className="bg-zinc-100 border border-zinc-300 hover:bg-zinc-200 text-zinc-800 px-4 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2 disabled:opacity-40"
          >
            <RefreshCcw size={12} className={cleaningOrphans ? "animate-spin" : ""} />
            {cleaningOrphans ? "Cleaning…" : "Clean Orphan Logs"}
          </button>
          <button
            data-testid="rebuild-stock-btn"
            onClick={rebuildStock}
            disabled={busy}
            className="bg-zinc-100 border border-zinc-300 hover:bg-zinc-200 text-zinc-800 px-4 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2 disabled:opacity-40"
          >
            <Wrench size={12} className={rebuilding ? "animate-spin" : ""} />
            {rebuilding ? "Rebuilding…" : "Rebuild Stock from Lots"}
          </button>
          <button
            data-testid="audit-refresh-btn"
            onClick={load}
            className="border border-zinc-300 bg-white text-zinc-700 px-4 py-2 text-xs uppercase tracking-wider font-mono hover:bg-zinc-50"
          >
            Refresh Audit
          </button>
        </div>
      </details>

      {/* Last result */}
      {lastResult && (
        <div className="bg-emerald-50 border border-emerald-300 px-4 py-3" data-testid="audit-result">
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-900 mb-1">
            {lastResult.kind === "recompute"
              ? "Master Fix Result"
              : lastResult.kind === "retroprice"
              ? "Re-price Historical Result"
              : lastResult.kind === "orphans"
              ? "Orphan Cleanup Result"
              : "Stock Rebuild Result"}
          </div>
          {lastResult.kind === "retroprice" && (
            <div className="text-xs font-mono text-zinc-800">
              Re-priced <strong>{lastResult.data.logs_priced}</strong> log(s) across{" "}
              <strong>{lastResult.data.per_rm.length}</strong> material(s) ·
              Qty priced: <strong>{Number(lastResult.data.qty_priced).toFixed(3)}</strong> ·
              Cost added: <strong>₹{Number(lastResult.data.cost_added).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
              {lastResult.data.per_rm.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-orange-700">Show breakdown by material</summary>
                  <table className="mt-2 w-full text-[11px] border border-zinc-300">
                    <thead className="bg-zinc-50 text-[9px] uppercase">
                      <tr>
                        <th className="px-2 py-1 text-left">Material</th>
                        <th className="px-2 py-1 text-right">Logs Priced</th>
                        <th className="px-2 py-1 text-right">Qty</th>
                        <th className="px-2 py-1 text-right">Cost Added (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastResult.data.per_rm.map((r) => (
                        <tr key={r.rm_id} className="border-t border-zinc-200">
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1 text-right font-mono">{r.logs_priced}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(r.qty_priced).toFixed(3)}</td>
                          <td className="px-2 py-1 text-right font-mono text-emerald-700">₹{Number(r.cost_added).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}
          {lastResult.kind === "recompute" && (
            <div className="text-xs font-mono text-zinc-800">
              Recomputed <strong>{lastResult.data.materials_recomputed}</strong> material(s) ·{" "}
              Changed <strong>{lastResult.data.materials_changed}</strong> ·{" "}
              Purged <strong>{lastResult.data.orphan_logs_deleted}</strong> orphan log(s) from{" "}
              <strong>{lastResult.data.orphan_cartons_purged}</strong> deleted carton(s).
              {lastResult.data.updates?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-orange-700">Show breakdown</summary>
                  <table className="mt-2 w-full text-[11px] border border-zinc-300">
                    <thead className="bg-zinc-50 text-[9px] uppercase">
                      <tr>
                        <th className="px-2 py-1 text-left">Material</th>
                        <th className="px-2 py-1 text-right">Received</th>
                        <th className="px-2 py-1 text-right">Used (alive)</th>
                        <th className="px-2 py-1 text-right">Old Stock</th>
                        <th className="px-2 py-1 text-right">New Stock</th>
                        <th className="px-2 py-1 text-right">Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastResult.data.updates.map((u) => (
                        <tr key={u.rm_id} className="border-t border-zinc-200">
                          <td className="px-2 py-1">{u.name}</td>
                          <td className="px-2 py-1 text-right font-mono">{u.total_received}</td>
                          <td className="px-2 py-1 text-right font-mono text-zinc-600">{u.used_by_alive_cartons}</td>
                          <td className="px-2 py-1 text-right font-mono">{u.old_stock}</td>
                          <td className="px-2 py-1 text-right font-mono text-emerald-700">{u.new_stock}</td>
                          <td className={`px-2 py-1 text-right font-mono ${u.delta > 0 ? "text-emerald-700" : "text-red-700"}`}>{u.delta > 0 ? "+" : ""}{u.delta}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}
          {lastResult.kind === "orphans" && (
            <div className="text-xs font-mono text-zinc-800">
              Cleaned <strong>{lastResult.data.orphan_cartons_cleaned}</strong> orphan carton(s) ·
              Refunded logs: <strong>{lastResult.data.logs_refunded}</strong> ·
              Qty refunded: <strong>{lastResult.data.qty_refunded}</strong> ·
              Lots restored: <strong>{lastResult.data.lots_restored}</strong>
            </div>
          )}
          {lastResult.kind === "rebuild" && (
            <div className="text-xs font-mono text-zinc-800">
              Updated <strong>{lastResult.data.materials_updated}</strong> material(s).
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function Stat({ label, value, highlight, warning }) {
  return (
    <div className={`border px-4 py-3 ${highlight ? "border-orange-500 bg-orange-50" : warning ? "border-amber-400 bg-amber-50" : "border-zinc-300 bg-white"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${highlight ? "text-orange-700" : warning ? "text-amber-700" : "text-zinc-900"}`}>{value ?? 0}</div>
    </div>
  );
}

function ConsumerDetail({ data }) {
  if (!data) return null;
  const d = data;
  return (
    <div className="space-y-3" data-testid="consumer-detail">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-700">
        {d.name} · Stock = Received − Used by alive cartons (legacy non-carton logs ignored)
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
        <Mini label="Received" value={`${Number(d.total_received).toFixed(3)} ${d.unit}`} />
        <Mini label="Used by alive cartons" value={`${Number(d.used_by_alive_cartons).toFixed(3)} ${d.unit}`} />
        <Mini label="Legacy / non-carton (ignored)" value={`${Number(d.used_legacy_no_carton).toFixed(3)} ${d.unit}`} />
        <Mini label="Expected stock" value={`${Number(d.expected_stock_per_formula).toFixed(3)} ${d.unit}`} highlight />
      </div>
      {d.used_orphan_cartons > 0 && (
        <div className="text-[11px] font-mono text-red-700 bg-red-50 border border-red-200 px-3 py-2">
          ⚠ {Number(d.used_orphan_cartons).toFixed(3)} {d.unit} still logged against deleted cartons —
          this means Master Fix has NOT been run yet, or those logs were created after the last run.
          Click <strong>Run Master Fix</strong> above to refund them.
        </div>
      )}
      {d.consumers.length === 0 ? (
        <div className="text-xs font-mono text-zinc-500">
          No alive cartons are consuming this raw material. The full drift, if any, comes from legacy logs (no carton_no) or orphan logs (deleted cartons).
        </div>
      ) : (
        <div className="bg-white border border-zinc-300 overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-zinc-100 text-[9px] font-mono uppercase tracking-wider text-zinc-700">
              <tr>
                <th className="text-left px-2 py-1.5">Carton (Box No)</th>
                <th className="text-left px-2 py-1.5">SKU</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-right px-2 py-1.5">Net kg</th>
                <th className="text-right px-2 py-1.5">Charged Qty</th>
                <th className="text-center px-2 py-1.5">Product Still Links Here?</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {d.consumers.map((c) => (
                <tr key={c.box_no} className="border-t border-zinc-200" data-testid={`consumer-${c.box_no}`}>
                  <td className="px-2 py-1.5 text-zinc-900">{c.box_no}</td>
                  <td className="px-2 py-1.5">{c.sku || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-2 py-1.5 uppercase text-[10px]">{c.status}</td>
                  <td className="px-2 py-1.5 text-right">{Number(c.net_weight_kg).toFixed(3)}</td>
                  <td className="px-2 py-1.5 text-right">{Number(c.qty_charged).toFixed(3)} {d.unit}</td>
                  <td className="px-2 py-1.5 text-center">
                    {c.product_still_links_to_this_rm ? (
                      <span className="text-emerald-700 text-[10px] uppercase font-mono">Yes</span>
                    ) : (
                      <span className="text-orange-700 text-[10px] uppercase font-mono" title="The product's carton_item_id no longer points to this RM — this is legacy consumption that won't repeat for new cartons of this SKU.">No · legacy link</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, highlight }) {
  return (
    <div className={`border px-2 py-1.5 ${highlight ? "border-orange-300 bg-orange-50" : "border-zinc-200 bg-white"}`}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-0.5 ${highlight ? "text-orange-800 font-semibold" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

const FIELDS = [
  { group: "Identity", rows: [
    [{ k: "name", label: "Legal Name" }, { k: "short_name", label: "Short Name" }],
    [{ k: "tagline", label: "Tagline" }, { k: "initials", label: "Initials (logo)" }],
  ]},
  { group: "Statutory", rows: [
    [{ k: "gstin", label: "GSTIN" }, { k: "pan", label: "PAN" }],
    [{ k: "cin", label: "CIN" }, { k: "website", label: "Website" }],
  ]},
  { group: "Contact", rows: [
    [{ k: "phone", label: "Phone" }, { k: "email", label: "Email" }],
    [{ k: "address", label: "Address", textarea: true, full: true }],
  ]},
  { group: "Bank Details", rows: [
    [{ k: "bank_name", label: "Bank Name" }, { k: "bank_branch", label: "Branch" }],
    [{ k: "bank_account", label: "Account No." }, { k: "bank_ifsc", label: "IFSC Code" }],
  ]},
];

function CompanyTab() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company/profile").then((r) => setProfile(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, []);

  if (!profile) return <div className="text-zinc-500 py-10 text-sm">Loading profile…</div>;

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company/profile", profile);
      setProfile(data);
      toast.success("Company profile updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {FIELDS.map((sec) => (
        <div key={sec.group} className="bg-white border border-zinc-300 p-5" data-testid={`section-${sec.group.toLowerCase().replace(" ", "-")}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-4">{sec.group}</div>
          <div className="space-y-3">
            {sec.rows.map((row, ri) => (
              <div key={ri} className={`grid gap-3 ${row.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
                {row.map((f) => (
                  <div key={f.k} className={f.full ? "md:col-span-2" : ""}>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{f.label}</label>
                    {f.textarea ? (
                      <textarea
                        data-testid={`field-${f.k}`}
                        rows={3}
                        value={profile[f.k] || ""}
                        onChange={(e) => setField(f.k, e.target.value)}
                        className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-orange-600"
                      />
                    ) : (
                      <input
                        data-testid={`field-${f.k}`}
                        value={profile[f.k] || ""}
                        onChange={(e) => setField(f.k, e.target.value)}
                        className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-orange-600"
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex justify-end">
        <button
          data-testid="save-company-btn"
          onClick={save}
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2 disabled:opacity-60"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null); // user being reset
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const submitReset = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/users/${target.id}/reset-password`, { password: pw });
      toast.success(`Password reset for ${target.email}`);
      setTarget(null);
      setPw("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-orange-50 border border-orange-200 px-4 py-3 text-xs font-mono text-orange-800 flex items-start gap-2">
        <ShieldCheck size={14} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Admin self-reset</div>
          <div className="mt-0.5 text-orange-700">To rotate your own admin password, ask in the chat box — the agent will run <span className="bg-orange-100 px-1">/app/backend/scripts/reset_admin_password.py</span> with your new password. This UI cannot reset the currently logged-in admin to prevent accidental lock-out.</div>
        </div>
      </div>
      <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Name</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Email</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Role</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Created</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-200" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2 font-medium text-zinc-950">{u.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2"><span className="text-[10px] font-mono uppercase border px-2 py-0.5 border-zinc-300 bg-zinc-50 text-zinc-700">{u.role}</span></td>
                <td className="px-3 py-2 text-[11px] font-mono text-zinc-600">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    data-testid={`reset-pwd-${u.id}`}
                    onClick={() => { setTarget(u); setPw(""); }}
                    className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100"
                  ><KeyRound size={11} /> Reset Password</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {target && (
        <div className="fixed inset-0 bg-zinc-950/60 z-50 flex items-center justify-center p-4">
          <form onSubmit={submitReset} className="bg-white border border-zinc-300 w-full max-w-md" data-testid="reset-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Reset password</div>
                <h2 className="text-lg font-semibold">{target.name}</h2>
                <div className="text-[11px] font-mono text-zinc-600">{target.email} · {target.role}</div>
              </div>
              <button type="button" onClick={() => { setTarget(null); setPw(""); }} className="text-zinc-500 hover:text-zinc-900"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block">New Password (min 6 chars)</label>
              <input
                data-testid="new-pwd-input"
                autoFocus
                type="text"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-orange-600"
                placeholder="e.g. Welcome@2026"
              />
              <div className="text-[11px] text-zinc-600">The user will continue using their current session until their token expires; ask them to log out & log back in with the new password.</div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2 bg-zinc-50">
              <button type="button" onClick={() => { setTarget(null); setPw(""); }} className="px-4 py-2 text-xs uppercase tracking-wider font-mono border border-zinc-300 hover:bg-zinc-100">Cancel</button>
              <button
                type="submit"
                disabled={busy}
                data-testid="submit-reset-btn"
                className="px-4 py-2 text-xs uppercase tracking-wider font-mono bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60 flex items-center gap-2"
              ><KeyRound size={12} /> {busy ? "Resetting…" : "Reset Password"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
